import { useState, useMemo, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Badge from "@mui/material/Badge";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  CalendarToday as CalendarTodayIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  DirectionsCar as DirectionsCarIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { CARD_TITLE_STYLE } from "../../constants/cardStyles";
import { GLOBAL_TOOLTIP_PROPS } from "../../config/tooltips.config.ts";
import { ViewToggle } from "./ViewToggle.tsx";
import { ViewANPRDialog } from "./ViewANPRDialog.tsx";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog.tsx";
import { AddANPRDialog } from "./AddANPRDialog.tsx";
import { formatDateDMY } from "../../utils/dateFormat.ts";
import { RECYCLE_BIN_RETENTION_MS } from "./FaceRecognitionTable.tsx";
import React from "react";
import { useModuleCrudPermissions } from "../../hooks/useModuleCrudPermissions.js";
import { userTimezoneHeaders } from "../../utils/userTimezoneHeader";
import { readSrIdFromUnknown } from "../../utils/srIdDisplay.ts";

/** Toolbar filter: chevron + clear without overlapping label (Recycle Bin pattern). */
const ANPR_TABLE_FILTER_SELECT_PR_CLEAR = "76px";
const ANPR_TABLE_FILTER_CLEAR_BTN_RIGHT_PX = 36;

const USE_CASE_CONFIG_API_BASE =
  process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";

/** API response item from GET /use-case-configuration/vehicles */
interface VehicleEnrollmentApiItem {
  id: string;
  license_plate: string;
  vehicle_category_id: string;
  vehicle_owner: string;
  permit_start_date: string;
  permit_end_date: string;
  address?: string | null;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  sr_id?: string | number;
}

/** API response item from GET /use-case-configuration/vehicle-categories (for id -> name mapping) */
interface VehicleCategoryApiItem {
  id: string;
  name: string;
}

export interface ANPRData {
  id: string;
  /** Backend `sr_id` — preferred label in ID column */
  srId?: string;
  licensePlate: string;
  category: string;
  vehicle: string;
  vehicleOwner: string;
  permitStartDate: Date;
  permitEndDate: Date;
  isFavorite?: boolean;
  address?: string;
  description?: string;
  /** Deletion timestamp (ms); set when moved to recycle bin; cleared on restore */
  deletedAt?: number;
}

// Sample data
const INITIAL_ANPR_DATA: ANPRData[] = [];


// Placeholder images for grid view - vehicle related
const PLACEHOLDER_IMAGES = [];

type ViewMode = "list" | "grid";
type Order = "asc" | "desc";
type OrderBy = keyof ANPRData;

const getCategoryColor = (category: string) => {
  switch (category.toLowerCase()) {
    case "staff":
      return { bgcolor: "#E6F4EA", color: "#1E8E3E" };
    case "visitor":
      return { bgcolor: "#E8F0FE", color: "#1967D2" };
    case "contractor":
      return { bgcolor: "#FEF7E0", color: "#F9AB00" };
    default:
      return { bgcolor: "#F1F3F4", color: "#5F6368" };
  }
};

function purgeExpiredRecycleBin<T extends { deletedAt?: number }>(
  items: T[],
): T[] {
  const now = Date.now();
  return items.filter(
    (item) =>
      !item.deletedAt || now - item.deletedAt <= RECYCLE_BIN_RETENTION_MS,
  );
}

export function ANPRTable() {
  const { create: canCreate, update: canUpdate, delete: canDelete } =
    useModuleCrudPermissions("use_cases");
  const [anprData, setAnprData] = useState<ANPRData[]>(() => {
    try {
      const savedData = localStorage.getItem("anpr-data");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        const mapped = parsed.map((item: any) => ({
          ...item,
          permitStartDate: new Date(item.permitStartDate),
          permitEndDate: new Date(item.permitEndDate),
          isFavorite: Boolean(item.isFavorite),
          deletedAt:
            item.deletedAt != null ? Number(item.deletedAt) : undefined,
        }));
        return purgeExpiredRecycleBin(mapped);
      }
    } catch (error) {
      console.error("Error loading ANPR data from localStorage:", error);
    }
    return INITIAL_ANPR_DATA;
  });

  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [vehiclesFetchError, setVehiclesFetchError] = useState<string | null>(
    null,
  );

  const fetchVehicles = useCallback(() => {
    const base = USE_CASE_CONFIG_API_BASE.trim();
    if (!base) {
      setVehiclesFetchError(
        "API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file.",
      );
      setVehiclesLoading(false);
      return;
    }
    setVehiclesFetchError(null);
    setVehiclesLoading(true);
    const baseUrl = base.replace(/\/$/, "");
    const categoriesUrl = `${baseUrl}/use-case-configuration/vehicle-categories`;
    const vehiclesUrl = `${baseUrl}/use-case-configuration/vehicles`;

    fetch(categoriesUrl, {
      headers: { Accept: "application/json", ...userTimezoneHeaders() },
    })
      .then((res) =>
        res.ok ? res.json() : Promise.reject(new Error(res.statusText)),
      )
      .then(
        (
          catRes:
            | { data?: VehicleCategoryApiItem[] }
            | VehicleCategoryApiItem[],
        ) => {
          const catList = Array.isArray(catRes) ? catRes : (catRes?.data ?? []);
          const categoryMap: Record<string, string> = {};
          catList.forEach((c: VehicleCategoryApiItem) => {
            if (c?.id && c?.name) categoryMap[c.id] = c.name;
          });
          return fetch(vehiclesUrl, {
            headers: { Accept: "application/json", ...userTimezoneHeaders() },
          })
            .then((res) => {
              if (!res.ok)
                throw new Error(res.statusText || "Failed to load vehicles");
              return res.json();
            })
            .then(
              (
                response:
                  | {
                      message?: string;
                      status?: string;
                      data?: VehicleEnrollmentApiItem[];
                    }
                  | VehicleEnrollmentApiItem[],
              ) => {
                const list = Array.isArray(response)
                  ? response
                  : (response?.data ?? []);
                const mapped: ANPRData[] = list
                  .filter((item) => !item.deleted_at)
                  .map((item) => ({
                    id: String(item.id),
                    srId: readSrIdFromUnknown(item),
                    licensePlate: item.license_plate ?? "",
                    category:
                      categoryMap[item.vehicle_category_id] ??
                      item.vehicle_category_id ??
                      "",
                    vehicle: "",
                    vehicleOwner: item.vehicle_owner ?? "",
                    permitStartDate: item.permit_start_date
                      ? new Date(item.permit_start_date)
                      : new Date(),
                    permitEndDate: item.permit_end_date
                      ? new Date(item.permit_end_date)
                      : new Date(),
                    isFavorite: false,
                    address: item.address ?? undefined,
                    description: item.description ?? undefined,
                  }));
                setAnprData(mapped);
                try {
                  localStorage.setItem("anpr-data", JSON.stringify(mapped));
                } catch (e) {
                  console.error(e);
                }
              },
            );
        },
      )
      .catch((err: Error) => {
        setVehiclesFetchError(
          err?.message ?? "Failed to load vehicle enrollments.",
        );
      })
      .finally(() => setVehiclesLoading(false));
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    try {
      localStorage.setItem("anpr-data", JSON.stringify(anprData));
    } catch (error) {
      console.error("Error saving ANPR data to localStorage:", error);
    }
  }, [anprData]);

  useEffect(() => {
    const loadFromStorage = () => {
      try {
        const savedData = localStorage.getItem("anpr-data");
        if (savedData) {
          const parsed = JSON.parse(savedData);
          const mapped = parsed.map((item: any) => ({
            ...item,
            permitStartDate: new Date(item.permitStartDate),
            permitEndDate: new Date(item.permitEndDate),
            isFavorite: Boolean(item.isFavorite),
            deletedAt:
              item.deletedAt != null ? Number(item.deletedAt) : undefined,
          }));
          setAnprData(purgeExpiredRecycleBin(mapped));
        }
      } catch (e) {
        console.error(e);
      }
    };
    window.addEventListener("configure-use-case-fav-changed", loadFromStorage);
    window.addEventListener(
      "configure-use-case-recycle-changed",
      loadFromStorage,
    );
    return () => {
      window.removeEventListener(
        "configure-use-case-fav-changed",
        loadFromStorage,
      );
      window.removeEventListener(
        "configure-use-case-recycle-changed",
        loadFromStorage,
      );
    };
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<OrderBy>("id");
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<{
    element: HTMLElement;
    anprItem: ANPRData;
    mouseX?: number;
    mouseY?: number;
  } | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState<ANPRData | null>(null);
  const [viewDialogData, setViewDialogData] = useState<ANPRData | null>(null);
  const [viewDialogDisplayId, setViewDialogDisplayId] = useState<string | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ANPRData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [filterDateRange, setFilterDateRange] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(() => {
    try {
      return localStorage.getItem("configure-anpr-show-favorites") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        "configure-anpr-show-favorites",
        String(showFavoritesOnly),
      );
    } catch (e) {
      console.error(e);
    }
  }, [showFavoritesOnly]);

  const handleViewModeChange = useCallback((newMode: ViewMode) => {
    setViewMode(newMode);
  }, []);

  const handleRequestSort = useCallback(
    (property: OrderBy) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy],
  );

  /** Client-side filtering: search and filters apply to full fetched dataset; no API call on search. */
  const filteredAndSorted = useMemo(() => {
    let filtered = anprData.filter((item) => !item.deletedAt);

    if (searchQuery) {
      filtered = filtered.filter(
        (item) =>
          item.licensePlate.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.srId ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.vehicle.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.vehicleOwner.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Date filter (values: "all" | "expiring-soon" | "next-3" | "next-6")
    if (filterDateRange !== "all") {
      const now = new Date();
      let start: number;
      let end: number;
      if (filterDateRange === "expiring-soon") {
        start = now.getTime();
        end = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate()).getTime();
      } else if (filterDateRange === "next-3") {
        start = now.getTime();
        end = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate()).getTime();
      } else if (filterDateRange === "next-6") {
        start = now.getTime();
        end = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate()).getTime();
      } else {
        start = 0;
        end = Number.MAX_SAFE_INTEGER;
      }
      filtered = filtered.filter((item) => {
        const date = item.permitEndDate.getTime();
        return date >= start && date <= end;
      });
    }

    // Category filter
    if (filterCategory !== "all") {
      filtered = filtered.filter((item) => item.category === filterCategory);
    }

    if (showFavoritesOnly) {
      filtered = filtered.filter((item) => item.isFavorite);
    }

    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null || bValue == null) return 0;
      if (aValue < bValue) {
        return order === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [
    anprData,
    searchQuery,
    order,
    orderBy,
    filterDateRange,
    filterCategory,
    showFavoritesOnly,
  ]);

  const favoritesCount = useMemo(
    () => anprData.filter((item) => item.isFavorite).length,
    [anprData],
  );

  /** Pagination over filtered results (client-side); recalculated when filters/search change. */
  const paginatedData = useMemo(() => {
    return filteredAndSorted.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredAndSorted, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, showFavoritesOnly, filterDateRange, filterCategory]);

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, anprItem: ANPRData) => {
      event.stopPropagation();
      setAnchorEl({ element: event.currentTarget, anprItem });
    },
    [],
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent, anprItem: ANPRData) => {
      event.preventDefault();
      setAnchorEl({
        element: event.currentTarget as HTMLElement,
        anprItem,
        mouseX: event.clientX,
        mouseY: event.clientY,
      });
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleToggleFavorite = useCallback((item: ANPRData) => {
    setAnprData((prev) =>
      prev.map((a) =>
        a.id === item.id ? { ...a, isFavorite: !a.isFavorite } : a,
      ),
    );
  }, []);

  const openViewDialog = useCallback(
    (anpr: ANPRData, listIndex: number) => {
      const displayId =
        anpr.srId?.trim() ||
        (/^ANPR-\d{3}$/.test(anpr.id ?? "")
          ? (anpr.id ?? "")
          : `ANPR-${String(page * rowsPerPage + listIndex + 1).padStart(3, "0")}`);
      setViewDialogDisplayId(displayId);
      setViewDialogData(anpr);
    },
    [page, rowsPerPage],
  );

  const handleView = useCallback(() => {
    if (anchorEl) {
      const idx = paginatedData.findIndex((a) => a.id === anchorEl.anprItem.id);
      openViewDialog(anchorEl.anprItem, idx >= 0 ? idx : 0);
    }
    handleMenuClose();
  }, [anchorEl, handleMenuClose, paginatedData, openViewDialog]);

  const handleEdit = useCallback(() => {
    if (anchorEl) {
      setEditDialogData(anchorEl.anprItem);
    }
    handleMenuClose();
  }, [anchorEl, handleMenuClose]);

  const handleDeleteClick = useCallback(() => {
    if (anchorEl) {
      setItemToDelete(anchorEl.anprItem);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  }, [anchorEl, handleMenuClose]);

  const handleDeleteConfirm = useCallback(() => {
    if (!itemToDelete) return;
    const base = USE_CASE_CONFIG_API_BASE.trim();
    if (!base) {
      setVehiclesFetchError("API base URL is not configured.");
      setItemToDelete(null);
      setDeleteDialogOpen(false);
      return;
    }
    setDeleteLoading(true);
    const enrollmentId = encodeURIComponent(itemToDelete.id);
    const url = `${base.replace(/\/$/, "")}/use-case-configuration/vehicles/${enrollmentId}`;
    fetch(url, {
      method: "DELETE",
      headers: { Accept: "application/json", ...userTimezoneHeaders() },
    })
      .then((res) => {
        if (res.status === 204) return;
        if (res.status === 422) {
          return res
            .json()
            .then((data: { detail?: Array<{ msg?: string }> }) => {
              throw new Error(data?.detail?.[0]?.msg ?? "Validation failed");
            });
        }
        throw new Error(res.statusText || "Failed to delete vehicle");
      })
      .then(() => {
        setItemToDelete(null);
        setDeleteDialogOpen(false);
        fetchVehicles();
        window.dispatchEvent(
          new CustomEvent("configure-use-case-recycle-changed"),
        );
      })
      .catch((err: Error) => {
        setVehiclesFetchError(
          err?.message ?? "Failed to delete vehicle enrollment.",
        );
        setItemToDelete(null);
        setDeleteDialogOpen(false);
      })
      .finally(() => setDeleteLoading(false));
  }, [itemToDelete, fetchVehicles]);

  const formatDate = (date: Date) => {
    return formatDateDMY(date);
  };

  const handleAddANPR = () => {
    fetchVehicles();
  };

  const handleUpdateANPR = () => {
    fetchVehicles();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleLicensePlateClick = (anpr: ANPRData) => {
    const idx = paginatedData.findIndex((a) => a.id === anpr.id);
    openViewDialog(anpr, idx >= 0 ? idx : 0);
  };

  // Get unique categories for filter
  const categories = useMemo(() => {
    const uniqueCats = Array.from(
      new Set(anprData.map((item) => item.category)),
    );
    return uniqueCats.sort();
  }, [anprData]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#fff",
      }}
    >
      {/* Header with Title and Info Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 400,
              color: "#FF8555",
            }}
          >
            ANPR (Automatic Number Plate Recognition)
          </Typography>
          <Tooltip
            title="Manage vehicle license plates for automated recognition"
            {...GLOBAL_TOOLTIP_PROPS}
          >
            <IconButton
              size="small"
              sx={{
                color: "#9E9E9E",
                padding: "2px",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search, Filters, and Actions Row */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          px: { xs: 1, sm: 2 },
          pb: 2,
          alignItems: "center",
        }}
      >
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: "100%", sm: "240px" },
            minWidth: { xs: 0, sm: 240 },
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
            "& .MuiOutlinedInput-root": {
              height: 36,
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: "#fff",
              borderRadius: "10px",
              "& fieldset": {
                borderColor: "#E5E7EB",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Favorites Filter */}
        <Tooltip
          title={showFavoritesOnly ? "Show all" : "Show favorites only"}
          {...GLOBAL_TOOLTIP_PROPS}
        >
          <IconButton
            size="small"
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              height: 36,
              width: 36,

              bgcolor: showFavoritesOnly ? "#FFE0D0" : "#fff",
              "&:hover": {
                bgcolor: showFavoritesOnly ? "#FFD0B8" : "#F5F5F5",
              },
            }}
          >
            <Badge
              badgeContent={favoritesCount}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "10px",
                  height: "16px",
                  minWidth: "16px",
                  borderRadius: "10px",
                  bgcolor: "#2932E5",
                },
              }}
            >
              {showFavoritesOnly ? (
                <StarIcon fontSize="small" sx={{ color: "#FF8555" }} />
              ) : (
                <StarBorderIcon fontSize="small" sx={{ color: "#9E9E9E" }} />
              )}
            </Badge>
          </IconButton>
        </Tooltip>

        {/* View Toggle */}
        <ViewToggle
          view={viewMode}
          onChange={handleViewModeChange}
          ariaLabel="Toggle ANPR view mode"
        />

        {/* Date Filter (permit) */}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: 100, sm: 140 }, position: "relative" }}
        >
          <Select
            value={filterDateRange}
            onChange={(e) => setFilterDateRange(e.target.value)}
            displayEmpty
            renderValue={(v) => {
              const labels: Record<string, string> = {
                all: "All Permit Dates",
                "expiring-soon": "Expiring Soon",
                "next-3": "Next 3 Months",
                "next-6": "Next 6 Months",
              };
              return labels[v ?? "all"] ?? "All Permit Dates";
            }}
            sx={{
              height: 36,
              minWidth: { xs: 100, sm: 140 },
              borderRadius: "10px",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: filterDateRange !== "all" ? "#FFE0D0" : "#fff",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingRight:
                  filterDateRange !== "all"
                    ? `${ANPR_TABLE_FILTER_SELECT_PR_CLEAR} !important`
                    : undefined,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
              },
              "&:hover": {
                bgcolor: filterDateRange !== "all" ? "#FFE0D0" : "#f5f5f5",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2932E5",
              },
            }}
            startAdornment={
              <CalendarTodayIcon
                sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }}
              />
            }
          >
            <MenuItem value="all">All Permit Dates</MenuItem>
            <MenuItem value="expiring-soon">Expiring Soon</MenuItem>
            <MenuItem value="next-3">Next 3 Months</MenuItem>
            <MenuItem value="next-6">Next 6 Months</MenuItem>
          </Select>
          {filterDateRange !== "all" ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFilterDateRange("all");
              }}
              aria-label="Clear permit date filter"
              sx={{
                position: "absolute",
                right: `${ANPR_TABLE_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                p: 0.25,
                width: 28,
                height: 28,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </FormControl>

        {/* Category Filter */}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: 120, sm: 150 }, position: "relative" }}
        >
          <Select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            displayEmpty
            sx={{
              height: 36,
              minWidth: { xs: 120, sm: 150 },
              borderRadius: "10px",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: filterCategory !== "all" ? "#FFE0D0" : "#fff",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingRight:
                  filterCategory !== "all"
                    ? `${ANPR_TABLE_FILTER_SELECT_PR_CLEAR} !important`
                    : undefined,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
              },
              "&:hover": {
                bgcolor: filterCategory !== "all" ? "#FFE0D0" : "#f5f5f5",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2932E5",
              },
            }}
            startAdornment={
              <FilterListIcon sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }} />
            }
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
          {filterCategory !== "all" ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFilterCategory("all");
              }}
              aria-label="Clear category filter"
              sx={{
                position: "absolute",
                right: `${ANPR_TABLE_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                p: 0.25,
                width: 28,
                height: 28,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </FormControl>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Add Vehicle Button */}
        {canCreate ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{
            bgcolor: "#2932E5",
            border: "0.8px solid #2932E5",
            borderRadius: "8px",
            color: "#fff",
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: 500,
            height: 36,
            px: { xs: 1.5, sm: 2 },
            boxShadow: "none",
            flexShrink: 0,
            "&:hover": {
              bgcolor: "#1f28b8",
              border: "0.8px solid #1f28b8",
              boxShadow: "none",
            },
          }}
        >
          Add Vehicle
        </Button>
        ) : null}
      </Box>

      {/* Error / Loading / Table or Grid View */}
      {vehiclesFetchError ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              color: "#4A5565",
              textAlign: "center",
            }}
          >
            {vehiclesFetchError}
          </Typography>
          <Button
            variant="contained"
            onClick={fetchVehicles}
            sx={{
              bgcolor: "#2932E5",
              textTransform: "none",
              "&:hover": { bgcolor: "#1f28b8" },
            }}
          >
            Retry
          </Button>
        </Box>
      ) : vehiclesLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#6B7280",
            }}
          >
            Loading vehicle enrollments...
          </Typography>
        </Box>
      ) : anprData.filter((i) => !i.deletedAt).length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            focusable="false"
            aria-hidden="true"
            width={150}
            height={150}
            style={{ display: "block", color: "#D1D5DB" }}
          >
            <path
              fill="currentColor"
              d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16m11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5M5 11l1.5-4.5h11L19 11z"
            />
          </Box>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            No data available
          </Typography>
        </Box>
      ) : filteredAndSorted.length === 0 &&
        showFavoritesOnly &&
        favoritesCount === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <StarBorderIcon sx={{ fontSize: 40, color: "#D1D5DB" }} />
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              color: "#4B5563",
              textAlign: "center",
            }}
          >
            No favorites yet
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            Click the star icon next to vehicles to add them to favorites.
          </Typography>
        </Box>
      ) : filteredAndSorted.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            width={150}
            height={150}
            focusable="false"
            aria-hidden="true"
            style={{ display: "block", color: "#D1D5DB" }}
          >
            <path
              fill="currentColor"
              d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16m11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5M5 11l1.5-4.5h11L19 11z"
            />
          </Box>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            No vehicles match your filters.
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#9CA3AF",
              textAlign: "center",
            }}
          >
            Try adjusting search, expiry, category, or show all vehicles.
          </Typography>
        </Box>
      ) : viewMode === "list" ? (
        <TableContainer
          sx={{
            flex: 1,
            overflow: "auto",
            maxWidth: "100%",
            minHeight: 0,
            overflowX: "auto",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: "fixed",
              width: "100%",
              minWidth: 800,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: "#FAFAFA",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#2932E5",
                    borderBottom: "1px solid #E5E7EB",
                    px: "10px",
                    py: "8px",
                    verticalAlign: "middle",
                    width: 100,
                    minWidth: 100,
                  }}
                >
                  ID
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#FAFAFA",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#2932E5",
                    borderBottom: "1px solid #E5E7EB",
                    px: "10px",
                    py: "8px",
                    verticalAlign: "middle",
                    cursor: "pointer",
                    width: 140,
                    minWidth: 140,
                  }}
                  onClick={() => handleRequestSort("licensePlate")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    License Plate
                    {orderBy === "licensePlate" &&
                      (order === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#FAFAFA",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#2932E5",
                    borderBottom: "1px solid #E5E7EB",
                    px: "10px",
                    py: "8px",
                    verticalAlign: "middle",
                    cursor: "pointer",
                    width: 120,
                    minWidth: 120,
                  }}
                  onClick={() => handleRequestSort("category")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Category
                    {orderBy === "category" &&
                      (order === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#FAFAFA",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#2932E5",
                    borderBottom: "1px solid #E5E7EB",
                    px: "10px",
                    py: "8px",
                    verticalAlign: "middle",
                    cursor: "pointer",
                    width: 180,
                    minWidth: 180,
                  }}
                  onClick={() => handleRequestSort("vehicleOwner")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Vehicle Owner
                    {orderBy === "vehicleOwner" &&
                      (order === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#FAFAFA",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#2932E5",
                    borderBottom: "1px solid #E5E7EB",
                    px: "10px",
                    py: "8px",
                    verticalAlign: "middle",
                    cursor: "pointer",
                    width: 110,
                    minWidth: 110,
                  }}
                  onClick={() => handleRequestSort("permitStartDate")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Permit Start
                    {orderBy === "permitStartDate" &&
                      (order === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: "#FAFAFA",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#2932E5",
                    borderBottom: "1px solid #E5E7EB",
                    px: "10px",
                    py: "8px",
                    verticalAlign: "middle",
                    cursor: "pointer",
                    width: 110,
                    minWidth: 110,
                  }}
                  onClick={() => handleRequestSort("permitEndDate")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Permit End
                    {orderBy === "permitEndDate" &&
                      (order === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    bgcolor: "#FAFAFA",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color:
                      hoveredRowId != null || anchorEl != null
                        ? "#2932E5"
                        : "transparent",
                    borderBottom: "1px solid #E5E7EB",
                    px: "10px",
                    py: "8px",
                    verticalAlign: "middle",
                    width: 80,
                    transition: "color 0.2s",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((item, index) => {
                const categoryStyle = getCategoryColor(item.category);
                const displayId =
                  item.srId?.trim() ||
                  (/^ANPR-\d{3}$/.test(item.id ?? "")
                    ? (item.id ?? "")
                    : `ANPR-${String(page * rowsPerPage + index + 1).padStart(3, "0")}`);
                return (
                  <TableRow
                    key={item.id}
                    onMouseEnter={() => setHoveredRowId(item.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                    sx={{
                      "&:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.02)",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        color: "#4A5565",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      {displayId}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        color: "#2932E5",
                        fontWeight: 600,
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        borderBottom: "1px solid #E5E7EB",
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => handleLicensePlateClick(item)}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {item.licensePlate ?? ""}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(item);
                          }}
                          sx={{
                            padding: "4px",
                            opacity: hoveredRowId === item.id ? 1 : 0,
                            transition: "opacity 0.2s",
                          }}
                        >
                          {item.isFavorite ? (
                            <StarIcon sx={{ fontSize: 16, color: "#FFB400" }} />
                          ) : (
                            <StarBorderIcon
                              sx={{ fontSize: 16, color: "#9E9E9E" }}
                            />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      <Chip
                        label={item.category ?? ""}
                        size="small"
                        sx={{
                          bgcolor: categoryStyle.bgcolor,
                          color: categoryStyle.color,
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "12px",
                          fontWeight: 500,
                          height: 24,
                          borderRadius: "4px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        color: "#4A5565",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      {item.vehicleOwner ?? ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        color: "#4A5565",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      {item.permitStartDate
                        ? formatDate(item.permitStartDate)
                        : ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        color: "#4A5565",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      {item.permitEndDate ? formatDate(item.permitEndDate) : ""}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, item)}
                        sx={{
                          opacity: hoveredRowId === item.id ? 1 : 0,
                          transition: "opacity 0.2s",
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 20, color: "#4A5565" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Grid View - same card design as Manage Use Case */
        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 0.5, sm: 0 } }}>
          <Grid container spacing={1}>
            {paginatedData.map((item, index) => {
              const displayId =
                item.srId?.trim() ||
                (/^ANPR-\d{3}$/.test(item.id ?? "")
                  ? (item.id ?? "")
                  : `ANPR-${String(page * rowsPerPage + index + 1).padStart(3, "0")}`);
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={2}
                  key={item.id}
                  sx={{ display: "flex", minWidth: 0 }}
                >
                  <Card
                    onMouseEnter={() => setHoveredRowId(item.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    onClick={() => handleLicensePlateClick(item)}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      minWidth: 0,
                      height: { xs: "200px", sm: "220px" },
                      minHeight: { xs: 180, sm: 220 },
                      bgcolor: "rgb(255, 255, 255)",
                      color: "rgb(33, 33, 33)",
                      boxShadow: "var(--Paper-shadow)",
                      backgroundImage: "var(--Paper-overlay)",
                      overflow: "hidden",
                      borderRadius: "14px",
                      transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      border: "1px solid rgb(229, 231, 235)",
                      "&:hover": {
                        boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                        transform: "translateY(-4px) scale(1.02)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        paddingLeft: { xs: 1, sm: "10px" },
                        paddingRight: { xs: 1, sm: "10px" },
                        pt: 1,
                        pb: 0,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          height: { xs: "96px", sm: "112.5px" },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#F2F2F2",
                          borderRadius: "8px",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(item);
                          }}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            color: item.isFavorite
                              ? "#FFB400"
                              : "rgba(255,255,255,0.95)",
                            bgcolor: "rgba(255,255,255,0.15)",
                            padding: "6px",
                            "&:hover": {
                              bgcolor: "rgba(255,255,255,0.3)",
                              color: item.isFavorite ? "#FFB400" : "#fff",
                            },
                          }}
                        >
                          {item.isFavorite ? (
                            <StarIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                        {/* Same license plate treatment as ViewANPRDialog */}
                        <Box
                          sx={{
                            bgcolor: "#FFEB3B",
                            border: "2px solid #212121",
                            borderRadius: "4px",
                            px: { xs: 1.25, sm: 1.5 },
                            py: { xs: 0.75, sm: 1 },
                            maxWidth: "calc(100% - 16px)",
                            zIndex: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: {
                                xs: "13px",
                                sm: "15px",
                              },
                              fontWeight: 700,
                              color: "#212121",
                              letterSpacing: { xs: "2px", sm: "3px" },
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                            }}
                            title={item.licensePlate ?? ""}
                          >
                            {item.licensePlate || "—"}
                          </Typography>
                        </Box>
                        {/* <Box
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 8,
                            zIndex: 1,
                            px: 1,
                            py: 0.25,
                            borderRadius: "20px",
                            bgcolor: "rgba(255, 179, 128, 0.92)",
                            color: "#fff",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "10px",
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            textTransform: "uppercase",
                            maxWidth: "calc(100% - 16px)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {(item.category || "ANPR").toUpperCase()}
                        </Box> */}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        p: { xs: 1, sm: 1.5 },
                        pt: { xs: 1, sm: 1.25 },
                        flex: 1,
                        minWidth: 0,
                        minHeight: { xs: 56, sm: 72 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: { xs: "12px", sm: "13px" },
                          fontWeight: 600,
                          color: "#2932E5",
                          mb: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {displayId}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: { xs: "12px", sm: "13px" },
                          color: "#4A5565",
                          mb: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {item.vehicleOwner}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: { xs: "12px", sm: "13px" },
                          color: "#4A5565",
                          mt: 0.25,
                          mb: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {item.category}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: { xs: "12px", sm: "13px" },
                          color: "#4A5565",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        ID: {displayId}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Pagination - only when data is loaded */}
      {!vehiclesLoading && !vehiclesFetchError && (
        <TablePagination
          component="div"
          count={filteredAndSorted.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
          sx={{
            borderTop: "1px solid #E5E7EB",
            bgcolor: "#F2F2F2",
            "& .MuiTablePagination-toolbar": {
              minHeight: { xs: 48, sm: 52 },
              px: { xs: 1, sm: 2 },
              flexWrap: "wrap",
              justifyContent: "flex-end",
            },
            "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
              {
                fontFamily: "Roboto, sans-serif",
                fontSize: { xs: "12px", sm: "14px" },
                color: "#4A5565",
                m: 0,
              },
            "& .MuiTablePagination-actions": {
              color: "#4A5565",
            },
          }}
        />
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl?.element}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorReference={
          anchorEl?.mouseX != null ? "anchorPosition" : "anchorEl"
        }
        anchorPosition={
          anchorEl?.mouseX != null && anchorEl?.mouseY != null
            ? { top: anchorEl.mouseY, left: anchorEl.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleView}>
          <VisibilityIcon sx={{ fontSize: 18, mr: 1 }} />
          View
        </MenuItem>
        {canUpdate ? (
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ fontSize: 18, mr: 1 }} />
          Edit
        </MenuItem>
        ) : null}
        {canDelete ? (
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon sx={{ fontSize: 18, mr: 1 }} />
          Delete
        </MenuItem>
        ) : null}
      </Menu>

      {/* Add ANPR Dialog */}
      <AddANPRDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleAddANPR}
        existingLicensePlates={anprData
          .filter((i) => !i.deletedAt)
          .map((i) => i.licensePlate)}
      />

      {/* Edit ANPR Dialog */}
      <AddANPRDialog
        open={!!editDialogData}
        onClose={() => setEditDialogData(null)}
        onSave={handleUpdateANPR}
        editData={editDialogData}
        existingLicensePlates={anprData
          .filter((i) => !i.deletedAt)
          .map((i) => i.licensePlate)}
      />

      {/* View Dialog */}
      <ViewANPRDialog
        open={!!viewDialogData}
        onClose={() => {
          setViewDialogData(null);
          setViewDialogDisplayId(null);
        }}
        anpr={viewDialogData}
        displayId={viewDialogDisplayId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        confirmLoading={deleteLoading}
        title="Delete ANPR Entry"
        message={
          itemToDelete
            ? `Are you sure you want to delete vehicle "${itemToDelete.licensePlate}"? Confirmation is required. The record will be moved to the Recycle Bin, retained for 60 days. After 60 days it will be permanently deleted.`
            : ""
        }
      />
    </Box>
  );
}
